import * as React from "react"
import {useCallback, useEffect, useRef, useState} from "react"
import {
    Box,
    Chip,
    Dialog,
    DialogContent,
    IconButton,
    Skeleton,
    Typography,
    Button,
    TablePagination,
    LinearProgress,
    TextField,
    InputAdornment
} from "@mui/material"
import {TableSeeker, IconSeeker} from "@ui-seeker/core"
import {AppLink} from "@ui-seeker/app"

import {selectedBatchForItems, batchItemEditDrawer, batchItemRecheckDialog} from "../@signals"
import {useBatchItems} from "../@hooks"
import LabelCountry from "@components/LabelCountry"
import EditMappingDrawer from "./EditMappingDrawer"
import RecheckDialog from "./RecheckDialog"
import {PRODUCT_MATCH_TYPES} from "@constants"


// Result status config (Mapped / Excluded / Pending)
const RESULT_CONFIG = {
    mapped: {label: 'Mapped', color: 'success'},
    excluded: {label: 'Excluded', color: 'default'},
    pending: {label: 'Pending', color: 'warning'}
}

// Method config (Auto / Manual)
const METHOD_CONFIG = {
    auto_ean: {label: 'Auto (EAN)', color: 'info'},
    auto_ia: {label: 'Auto (IA)', color: 'secondary'},
    manual: {label: 'Manual', color: 'primary'},
    pending: {label: '-', color: 'default'}
}

// Method filter options (Auto / Manual)
const METHOD_FILTER_OPTIONS = [
    {value: 'all', label: 'All'},
    {value: 'auto', label: 'Auto'},
    {value: 'manual', label: 'Manual'}
]

// Result filter options (Mapped / Excluded / Pending)
const RESULT_FILTER_OPTIONS = [
    {value: 'all', label: 'All'},
    {value: 'mapped', label: 'Mapped'},
    {value: 'excluded', label: 'Excluded'},
    {value: 'pending', label: 'Pending'}
]

// Helper to determine result status from item data
function getResultStatus(item) {
    if (!item.finished) return 'pending'
    if (item.notFound) return 'excluded'
    return 'mapped'
}

// Helper to determine method from item data
function getMethod(item) {
    if (!item.finished) return 'pending'
    if (item.auto) {
        return item.ean ? 'auto_ean' : 'auto_ia'
    }
    return 'manual'
}


function ImageThumb({src, alt}) {
    const [open, setOpen] = useState(false)
    const [hasError, setHasError] = useState(false)

    if (!src || src === "N/A" || hasError) {
        return (
            <Box sx={{width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', borderRadius: 1}}>
                <IconSeeker icon="image" sx={{color: 'text.disabled', fontSize: '1rem'}}/>
            </Box>
        )
    }
    return (
        <>
            <Box onClick={() => setOpen(true)} sx={{width: 50, height: 50, cursor: 'pointer', overflow: 'hidden', borderRadius: 1, border: theme => `1px solid ${theme.palette.divider}`}}>
                <Box component="img" src={src} alt={alt || ''} onError={() => setHasError(true)} sx={{width: 1, height: 1, objectFit: 'cover'}}/>
            </Box>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg">
                <DialogContent sx={{p: 0}}>
                    <Box component="img" src={src} alt={alt || ''} sx={{width: '100%', height: '100%', objectFit: 'contain'}}/>
                </DialogContent>
            </Dialog>
        </>
    )
}


// Highlighted variant tag — validation-critical info (size/shade traps)
function VariantTag({value}) {
    if (!value) return null
    return (
        <Typography variant="caption" sx={{
            display: 'inline-block', mt: 0.25, px: 0.5, borderRadius: 0.5,
            bgcolor: 'action.selected', color: 'text.primary', fontWeight: 600,
            lineHeight: 1.5, maxWidth: 240, overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap'
        }}>
            {value}
        </Typography>
    )
}

function ResultChip({item}) {
    const result = getResultStatus(item)
    const config = RESULT_CONFIG[result] || {label: result, color: 'default'}
    return <Chip label={config.label} size="small" color={config.color}/>
}

function MethodChip({item}) {
    const method = getMethod(item)
    const config = METHOD_CONFIG[method] || {label: '-', color: 'default'}
    if (method === 'pending') return <Typography variant="body2" color="text.secondary">-</Typography>
    return <Chip label={config.label} size="small" color={config.color} variant="outlined"/>
}


function ConfidenceBar({value}) {
    if (value == null || value === 0) return <Typography variant="body2">-</Typography>
    const percent = Math.round(value * 100)
    const color = percent >= 90 ? 'success' : percent >= 70 ? 'warning' : 'error'
    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 80}}>
            <LinearProgress
                variant="determinate"
                value={percent}
                color={color}
                sx={{flex: 1, height: 6, borderRadius: 3}}
            />
            <Typography variant="caption" sx={{minWidth: 35}}>{percent}%</Typography>
        </Box>
    )
}


function timeAgo(ts) {
    if (!ts) return '-'
    const now = Date.now()
    const then = new Date(ts).getTime()
    const diff = Math.max(0, Math.floor((now - then) / 1000))
    if (diff < 60) return `${diff}s ago`
    const m = Math.floor(diff / 60)
    if (m < 60) return `${m}m ago`
    const h = Math.floor(m / 60)
    if (h < 24) return `${h}h ago`
    const d = Math.floor(h / 24)
    return `${d}d ago`
}


function SummaryBar({summary}) {
    if (!summary) return null

    const totalMapped = (summary.autoMappedEan || 0) + (summary.autoMappedIa || 0) + (summary.manual || 0)
    const totalAuto = (summary.autoMappedEan || 0) + (summary.autoMappedIa || 0) + (summary.autoExcluded || 0)

    return (
        <Box sx={{display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: theme => `1px solid ${theme.palette.divider}`}}>
            {/* Total */}
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                <Typography variant="body2" color="text.secondary">Total:</Typography>
                <Typography variant="body2" fontWeight="bold">{summary.total || 0}</Typography>
            </Box>

            {/* Divider */}
            <Box sx={{borderLeft: 1, borderColor: 'divider'}}/>

            {/* By Result */}
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                <Typography variant="body2" color="text.secondary">Mapped:</Typography>
                <Typography variant="body2" fontWeight="bold" color="success.main">{totalMapped}</Typography>
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                <Typography variant="body2" color="text.secondary">Excluded:</Typography>
                <Typography variant="body2" fontWeight="bold" color="text.secondary">{summary.autoExcluded || 0}</Typography>
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                <Typography variant="body2" color="text.secondary">Pending:</Typography>
                <Typography variant="body2" fontWeight="bold" color="warning.main">{summary.pending || 0}</Typography>
            </Box>

            {/* Divider */}
            <Box sx={{borderLeft: 1, borderColor: 'divider'}}/>

            {/* By Method */}
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                <Typography variant="body2" color="text.secondary">Auto:</Typography>
                <Typography variant="body2" fontWeight="bold" color="info.main">{totalAuto}</Typography>
                <Typography variant="caption" color="text.secondary">
                    (EAN: {summary.autoMappedEan || 0}, IA: {summary.autoMappedIa || 0}, Excl: {summary.autoExcluded || 0})
                </Typography>
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                <Typography variant="body2" color="text.secondary">Manual:</Typography>
                <Typography variant="body2" fontWeight="bold" color="primary.main">{summary.manual || 0}</Typography>
            </Box>
        </Box>
    )
}


function FilterChips({options, value, onChange, label}) {
    return (
        <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
            <Typography variant="body2" color="text.secondary" sx={{minWidth: 55}}>{label}:</Typography>
            {options.map(option => (
                <Chip
                    key={option.value}
                    label={option.label}
                    size="small"
                    variant={value === option.value ? 'filled' : 'outlined'}
                    color={value === option.value ? 'primary' : 'default'}
                    onClick={() => onChange(option.value)}
                    sx={{cursor: 'pointer'}}
                />
            ))}
        </Box>
    )
}


export default function BatchItemsTable() {
    const containerRef = useRef(null)
    const {
        items,
        pagination,
        summary,
        methodFilter,
        resultFilter,
        search,
        isLoading,
        selectedBatch,
        fetchItems,
        setPage,
        setPageSize,
        setMethodFilter,
        setResultFilter,
        setSearch,
        closeItemsView
    } = useBatchItems()

    const batchType = selectedBatch?.batchType || 'sku2raw'
    const isRaw2Sku = batchType === 'raw2sku'
    // Both product↔product directions share PRODUCTS_TO_BE_MATCHED and this layout;
    // only which side anchored the batch differs.
    const isRaw2Raw = PRODUCT_MATCH_TYPES.includes(batchType)

    // Client-side free-text filter (comma = AND) across product / sku / site fields.
    // The hook loads the whole batch when a term is present, so `items` holds everything.
    const searchTerms = (search || '').toLowerCase().split(',').map(t => t.trim()).filter(Boolean)
    const isSearching = searchTerms.length > 0
    const filteredItems = !isSearching ? items : items.filter(it => {
        const hay = [it.productName, it.skuName, it.siteName, it.competitorSite]
            .filter(Boolean).join(' ').toLowerCase()
        return searchTerms.every(term => hay.includes(term))
    })

    useEffect(() => {
        if (!selectedBatch) return
        if (!containerRef.current) return
        containerRef.current.scrollIntoView({behavior: 'smooth', block: 'start'})
    }, [selectedBatch])

    const handleClose = useCallback(() => {
        closeItemsView()
        window.scrollTo({top: 0, behavior: 'smooth'})
    }, [closeItemsView])

    const handleRefresh = useCallback(() => {
        fetchItems()
    }, [fetchItems])

    const handleChangePage = useCallback((event, newPage) => {
        setPage(newPage + 1) // MUI TablePagination is 0-indexed, our API is 1-indexed
    }, [setPage])

    const handleChangeRowsPerPage = useCallback((event) => {
        setPageSize(parseInt(event.target.value, 10))
    }, [setPageSize])

    if (!selectedBatch) return null

    if (isLoading && items.length === 0) {
        return (
            <Box ref={containerRef} sx={{mt: 3}}>
                <Skeleton variant="rectangular" height={400}/>
            </Box>
        )
    }

    // Build columns based on batch type
    const columns = [
        // 0 - Result (Mapped / Excluded / Pending)
        {
            name: 'finished',
            label: 'Result',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value, tableMeta) => {
                    const rowData = filteredItems[tableMeta.rowIndex]
                    return <ResultChip item={rowData}/>
                },
                setCellHeaderProps: () => ({align: 'center'}),
                setCellProps: () => ({align: 'center'})
            }
        },
        // 1 - Method (Auto/Manual)
        {
            name: 'auto',
            label: 'Method',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value, tableMeta) => {
                    const rowData = filteredItems[tableMeta.rowIndex]
                    return <MethodChip item={rowData}/>
                },
                setCellHeaderProps: () => ({align: 'center'}),
                setCellProps: () => ({align: 'center'})
            }
        },
        // 2 - Source (SKU for sku2raw, Product for raw2sku)
        {
            name: isRaw2Sku ? 'productName' : 'skuName',
            label: isRaw2Sku ? 'Product' : (isRaw2Raw ? 'Own Product' : 'SKU'),
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value, tableMeta) => {
                    const rowData = filteredItems[tableMeta.rowIndex]
                    const image = isRaw2Sku ? rowData?.productImage : rowData?.skuImage
                    const url = isRaw2Sku ? rowData?.productUrl : (isRaw2Raw ? rowData?.ownUrl : null)
                    const variant = isRaw2Sku ? rowData?.productVariant : rowData?.skuVariant
                    return (
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, maxWidth: 300}}>
                            <ImageThumb src={image} alt={value}/>
                            <Box sx={{overflow: 'hidden'}}>
                                {url ? (
                                    <AppLink to={url} target="_blank">
                                        <Typography variant="body2" sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical'
                                        }}>
                                            {value}
                                            <IconSeeker icon="up-right-from-square" sx={{ml: 0.5, fontSize: '0.7rem', color: 'text.disabled'}}/>
                                        </Typography>
                                    </AppLink>
                                ) : (
                                    <Typography variant="body2" sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical'
                                    }}>{value || '-'}</Typography>
                                )}
                                {variant && <VariantTag value={variant}/>}
                            </Box>
                        </Box>
                    )
                },
                setCellHeaderProps: () => ({align: 'left'}),
                setCellProps: () => ({align: 'left'})
            }
        },
        // 2 - Result (Product for sku2raw, SKU for raw2sku)
        {
            name: isRaw2Sku ? 'skuName' : 'productName',
            label: isRaw2Sku ? 'SKU Proposed' : (isRaw2Raw ? 'Competitor' : 'Product Proposed'),
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value, tableMeta) => {
                    const rowData = filteredItems[tableMeta.rowIndex]
                    const image = isRaw2Sku ? rowData?.skuImage : rowData?.productImage
                    const url = isRaw2Sku ? null : rowData?.productUrl
                    const variant = isRaw2Sku ? rowData?.skuVariant : rowData?.productVariant
                    const hasValue = value && value !== '-'
                    if (!hasValue) return <Typography variant="body2" color="text.secondary">-</Typography>
                    return (
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, maxWidth: 300}}>
                            <ImageThumb src={image} alt={value}/>
                            <Box sx={{overflow: 'hidden'}}>
                                {url ? (
                                    <AppLink to={url} target="_blank">
                                        <Typography variant="body2" sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical'
                                        }}>
                                            {value}
                                            <IconSeeker icon="up-right-from-square" sx={{ml: 0.5, fontSize: '0.7rem', color: 'text.disabled'}}/>
                                        </Typography>
                                    </AppLink>
                                ) : (
                                    <Typography variant="body2" sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical'
                                    }}>{value}</Typography>
                                )}
                                {variant && <VariantTag value={variant}/>}
                            </Box>
                        </Box>
                    )
                },
                setCellHeaderProps: () => ({align: 'left'}),
                setCellProps: () => ({align: 'left'})
            }
        },
        // 3 - Site (raw2raw: the COMPETITOR/target site — the own site is implicit)
        {
            name: 'siteName',
            label: isRaw2Raw ? 'Competitor site' : 'Site',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value, tableMeta) => {
                    const rowData = filteredItems[tableMeta.rowIndex]
                    const label = isRaw2Raw ? (rowData?.competitorSite || value) : value
                    return label ? <LabelCountry country={label} label={label}/> : '-'
                },
                setCellHeaderProps: () => ({align: 'center'}),
                setCellProps: () => ({align: 'center'})
            }
        },
        // 4 - Confidence
        {
            name: 'iaDistance',
            label: 'Confidence',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value) => <ConfidenceBar value={value}/>,
                setCellHeaderProps: () => ({align: 'center'}),
                setCellProps: () => ({align: 'center'})
            }
        },
        // 5 - User
        {
            name: 'username',
            label: 'User',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value) => (
                    <Typography variant="body2">
                        {!value || value === 'N/A' ? 'System' : value}
                    </Typography>
                ),
                setCellHeaderProps: () => ({align: 'center'}),
                setCellProps: () => ({align: 'center'})
            }
        },
        // 6 - Date
        {
            name: 'ts',
            label: 'Date',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value) => (
                    <Typography variant="body2" sx={{whiteSpace: 'nowrap'}}>
                        {timeAgo(value)}
                    </Typography>
                ),
                setCellHeaderProps: () => ({align: 'center'}),
                setCellProps: () => ({align: 'center'})
            }
        },
        // 7 - Actions
        {
            name: 'idMapping',
            label: 'Actions',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value, tableMeta) => {
                    if (!value) return null
                    const rowData = filteredItems[tableMeta.rowIndex]
                    return (
                        <Box sx={{display: 'flex', gap: 0.5}}>
                            {/* The product↔product directions have no single-item mapper
                                endpoint — the API rejects them */}
                            {!PRODUCT_MATCH_TYPES.includes(batchType) && (
                                <IconButton
                                    size="small"
                                    color="secondary"
                                    title="Re-check Mapping"
                                    onClick={() => {
                                        batchItemRecheckDialog.value = { item: rowData, batchType }
                                    }}
                                >
                                    <IconSeeker icon="rotate"/>
                                </IconButton>
                            )}
                            <IconButton
                                size="small"
                                color="primary"
                                title="Edit Mapping"
                                onClick={() => {
                                    batchItemEditDrawer.value = { item: rowData, batchType }
                                }}
                            >
                                <IconSeeker icon="pen-to-square"/>
                            </IconButton>
                        </Box>
                    )
                },
                setCellHeaderProps: () => ({align: 'center'}),
                setCellProps: () => ({align: 'center'})
            }
        }
    ]

    return (
        <Box ref={containerRef} sx={{mt: 3}}>
            {/* Header */}
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1}}>
                <Typography variant="h6">
                    All Items for Batch #{selectedBatch.batchID}
                    {selectedBatch.batchName ? ` - ${selectedBatch.batchName}` : ''}
                    <Chip label={batchType} size="small" sx={{ml: 1}} variant="outlined"/>
                </Typography>
                <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                    <Button
                        size="small"
                        color="primary"
                        startIcon={<IconSeeker icon={isLoading ? 'spinner' : 'arrow-rotate-right'} isSpin={isLoading}/>}
                        onClick={handleRefresh}
                        disabled={isLoading}
                    >
                        Refresh
                    </Button>
                    <Button size="small" color="inherit" startIcon={<IconSeeker icon="circle-xmark"/>} onClick={handleClose}>
                        Close
                    </Button>
                </Box>
            </Box>

            {/* Summary */}
            <SummaryBar summary={summary}/>

            {/* Filters */}
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2}}>
                <Box sx={{display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center'}}>
                    <FilterChips options={RESULT_FILTER_OPTIONS} value={resultFilter} onChange={setResultFilter} label="Result"/>
                    <FilterChips options={METHOD_FILTER_OPTIONS} value={methodFilter} onChange={setMethodFilter} label="Method"/>
                    <TextField
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search product, SKU, site... (use comma for AND: text1,text2)"
                        sx={{minWidth: 320}}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IconSeeker icon="search" sx={{fontSize: '0.9rem', color: 'text.disabled'}}/>
                                </InputAdornment>
                            ),
                            endAdornment: search ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearch('')} title="Clear">
                                        <IconSeeker icon="xmark" sx={{fontSize: '0.8rem'}}/>
                                    </IconButton>
                                </InputAdornment>
                            ) : null
                        }}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {isSearching ? `${filteredItems.length} of ${pagination.totalItems}` : pagination.totalItems} items{isSearching ? ' matched' : ' total'}
                </Typography>
            </Box>

            {/* Loading indicator */}
            {isLoading && <LinearProgress sx={{mb: 1}}/>}

            {/* Table */}
            <TableSeeker
                data={filteredItems}
                title={null}
                columns={columns}
                options={{
                    selectableRows: 'none',
                    pagination: false, // We use our own pagination
                    filter: false,
                    search: false,
                    print: false,
                    download: false,
                    viewColumns: false
                }}
                textLabels={{
                    body: {noMatch: isLoading ? 'Loading...' : 'No items found.'}
                }}
            />

            {/* Pagination — hidden while searching (all items loaded, filtered client-side) */}
            {!isSearching && (
                <TablePagination
                    component="div"
                    count={pagination.totalItems || 0}
                    page={(pagination.page || 1) - 1}
                    onPageChange={handleChangePage}
                    rowsPerPage={pagination.pageSize || 50}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[25, 50, 100]}
                />
            )}

            {/* Edit Mapping Drawer */}
            <EditMappingDrawer fetchItems={fetchItems} />

            {/* Recheck Dialog */}
            <RecheckDialog fetchItems={fetchItems} />
        </Box>
    )
}
